import { IconBadge, Separator } from '@monksflow/monks-ui';
import type { PropsWithChildren, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

interface FormSectionV2Props extends PropsWithChildren {
  icon: ReactNode;
  title: string;
  description?: string;
  headerAction?: ReactNode;
}

/**
 * Titled block of related fields: an icon badge, a heading, and the fields
 * indented under it.
 *
 * The V1 section draws the same shape with MUI's `ListItem` trio; here the
 * layout is Tailwind and the badge is Tessra's own, so the two cannot be kept
 * in one component.
 */
export const FormSectionV2 = ({
  icon,
  title,
  description,
  headerAction,
  children,
}: FormSectionV2Props) => (
  <section className="flex flex-col gap-md">
    <div className="flex items-start gap-sm">
      {/* `muted` and the 40px step, matching the subtle grey square the V1 section
          draws. IconBadge's default variant is an inverted dark fill, and its
          `lg` is 54px. */}
      <IconBadge variant="muted" size="default">
        {icon}
      </IconBadge>
      <div className="flex min-w-0 flex-1 flex-col gap-3xs">
        <h3 className="text-base font-medium text-foreground">{title}</h3>
        {description && <p className="text-sm text-muted-foreground">{description}</p>}
      </div>
      {headerAction}
    </div>
    <div className="flex flex-col gap-md pl-14">{children}</div>
  </section>
);

FormSectionV2.displayName = 'FormSectionV2';

/** Divider plus a heading, separating groups of fields inside one section. */
export const FormSubsectionV2 = ({ children }: PropsWithChildren) => (
  <div className="flex flex-col gap-sm">
    <Separator />
    <span className="text-sm font-medium text-foreground">{children}</span>
  </div>
);

FormSubsectionV2.displayName = 'FormSubsectionV2';

export const RequiredFieldsLegendV2 = () => {
  const { t } = useTranslation('audiences');

  return (
    <span className="text-xs text-muted-foreground">
      <span className="text-destructive">*</span>
      {t('audiences.requiredFieldsLegend.requiredFields')}
    </span>
  );
};

RequiredFieldsLegendV2.displayName = 'RequiredFieldsLegendV2';
